import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";
import Marquee from "./Marquee";

export default function Hero({ hero, marquee }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });

  return (
    <>
      <section
        id="top"
        className="relative min-h-screen flex flex-col justify-end overflow-hidden grain bg-[#EBECE8]"
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          mx.set(((e.clientX - r.left) / r.width - 0.5) * 26);
          my.set(((e.clientY - r.top) / r.height - 0.5) * 16);
        }}
        onMouseLeave={() => {
          mx.set(0);
          my.set(0);
        }}
        data-testid="hero-section"
      >
        <motion.div style={{ x: sx, y: sy }} className="absolute inset-0 scale-[1.08]">
          <img
            src={imgUrl(hero.image)}
            alt="Rénovation de parking par CITEK"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#EBECE8]/95 via-[#EBECE8]/82 to-[#EBECE8]/96" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#EBECE8]/85 via-[#EBECE8]/45 to-transparent" />
        <div className="absolute inset-0 grid-overlay opacity-70" />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-5 md:px-8 pt-40 pb-16">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-tech text-sm md:text-base uppercase tracking-[0.24em] text-citek-greenDark mb-6"
            data-testid="hero-eyebrow"
          >
            {hero.eyebrow}
          </motion.p>

          <h1 className="font-display font-extrabold uppercase text-zinc-900 leading-[1.04] tracking-tight text-[clamp(1.85rem,5.2vw,4.4rem)] max-w-5xl">
            {hero.titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.95, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`hero-title-line-${i}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-zinc-600"
            data-testid="hero-subtitle"
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("#contact")}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-citek-green text-black font-semibold text-base px-8 py-4 rounded-md hover:bg-citek-greenBright transition-colors"
            >
              {hero.ctaPrimary}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("#metiers")}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-zinc-900/25 text-zinc-900 font-medium text-base px-8 py-4 rounded-md hover:border-citek-greenDark hover:text-citek-greenDark transition-colors"
            >
              {hero.ctaSecondary}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 font-tech text-xs md:text-sm uppercase tracking-[0.2em] text-zinc-500"
          >
            <span className="flex items-center gap-2" data-testid="hero-badge">
              <span className="h-2 w-2 rounded-full bg-citek-greenDark animate-pulse" />
              {hero.badge}
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="relative z-10 flex justify-center pb-6"
        >
          <ArrowDown size={20} className="text-citek-greenDark animate-bounce" />
        </motion.div>
      </section>

      <Marquee items={marquee} />
    </>
  );
}
